import type { ReactElement } from 'react';
import { Outlet, useLocation } from 'react-router';

import { AdminOnlyPage } from '@/pages/common/admin-only';

/** 깨진 퍼센트 인코딩은 그대로 둔다 — 판정 때문에 화면이 터지면 안 된다. */
const decode = (segment: string): string => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

/**
 * 주소의 어느 칸이든 `admin` 인가 — `/admin`, `/library/admin`, `/my/admin/x` 모두.
 *
 * **한 칸 전체**일 때만 본다. `/library/admin-guide` 처럼 단어 일부면 평범한 자료 주소다.
 * 대소문자·퍼센트 인코딩(`%61dmin`)으로 바꿔 쓴 것도 같은 칸으로 본다.
 * 대가: 주소에 들어가는 이름(자료·영상 slug, 포트폴리오 projectId 등)을 딱 `admin` 으로 지으면
 *   그 페이지는 열리지 않는다(2026-09-22 오너 결정). 지금 그런 이름은 없다.
 */
export const hasAdminSegment = (pathname: string): boolean =>
  pathname.split('/').some((segment) => decode(segment).toLowerCase() === 'admin');

/**
 * 주소 어느 칸에든 `admin` 이 있으면 원래 화면 대신 운영자 안내를 보인다.
 *
 * 라우터 **맨 바깥**에 둔다 — 경로마다 막으면 새 경로를 더할 때 빠뜨린 곳이 조용히 열린다
 * (어드민의 AccessGate 와 같은 판단). 그래서 `/library/admin` 이 영상 상세로, `/admin` 이 404 로
 * 새지 않는다. 운영의 `/admin/**` 은 nginx 가 어드민 앱으로 보내 여기까지 오지 않는다.
 */
export function AdminPathGuard(): ReactElement {
  const { pathname } = useLocation();
  return hasAdminSegment(pathname) ? <AdminOnlyPage /> : <Outlet />;
}
